import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Upload, FileDown, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { createProspecto } from "@/lib/api";
import { cleanPhone } from "@/lib/format";
import { useAuth, isAdmin } from "@/lib/auth";

// Cabeçalhos aceitos (case-insensitive, com/sem acento)
const HEADER_ALIASES = {
  nome: ["nome", "prospecto", "cliente", "nome do prospecto"],
  contato: ["contato", "telefone", "telefone/whatsapp", "whatsapp", "celular", "fone"],
  localidade: ["localidade", "endereco", "endereço", "bairro", "regiao", "região", "local"],
  motivo: ["motivo", "razao", "razão", "motivo da inviabilidade"],
  vendedor: ["vendedor", "vendedora", "responsavel", "responsável"],
  observacoes: ["observacoes", "observações", "obs", "notas"],
  data_registro: ["data_registro", "data", "data do registro", "registrado em"],
  reavaliar_em: ["reavaliar_em", "reavaliar em", "reavaliar"],
};

const REQUIRED_FIELDS = ["nome", "contato", "localidade", "motivo"];

const normalizeKey = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const mapHeader = (rawHeader) => {
  const norm = normalizeKey(rawHeader);
  for (const [canonical, aliases] of Object.entries(HEADER_ALIASES)) {
    if (aliases.some((a) => normalizeKey(a) === norm)) return canonical;
  }
  return null;
};

const parseDateCell = (val) => {
  if (val === null || val === undefined || val === "") return "";
  // Se veio como serial Excel, xlsx já pode converter; garantimos string YYYY-MM-DD
  if (val instanceof Date) return val.toISOString().slice(0, 10);
  const s = String(val).trim();
  // aceita DD/MM/YYYY
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  // já ISO
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return "";
};

const TEMPLATE_HEADERS = ["nome", "contato", "localidade", "motivo", "vendedor", "observacoes", "data_registro", "reavaliar_em"];
const TEMPLATE_SAMPLE = [
  {
    nome: "Padaria Pão Quente",
    contato: "5511999998888",
    localidade: "Vila Madalena",
    motivo: "Sem infraestrutura de fibra",
    vendedor: "Ana Souza",
    observacoes: "Retomar quando ampliar rede",
    data_registro: "2026-02-20",
    reavaliar_em: "2026-05-20",
  },
];

const buildTemplateCSV = () => {
  const BOM = "\uFEFF";
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = TEMPLATE_HEADERS.map(esc).join(";");
  const body = TEMPLATE_SAMPLE.map((r) => TEMPLATE_HEADERS.map((h) => esc(r[h])).join(";")).join("\r\n");
  return BOM + header + "\r\n" + body;
};

const downloadTemplate = () => {
  const csv = buildTemplateCSV();
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "modelo-importacao-prospectos.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export default function ImportarProspectosDialog({ open, onOpenChange, onImported }) {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]); // linhas parseadas com {data, errors, ok}
  const [fileName, setFileName] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const reset = () => {
    setRows([]);
    setFileName("");
    setImportResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const validateRow = (data) => {
    const errors = [];
    for (const f of REQUIRED_FIELDS) {
      if (!String(data[f] || "").trim()) errors.push(`Campo obrigatório: ${f}`);
    }
    if (data.contato && !cleanPhone(data.contato)) {
      errors.push("Telefone inválido");
    }
    // Vendedora força o próprio nome; Admin pode preencher qualquer vendedor
    if (admin) {
      if (!String(data.vendedor || "").trim()) errors.push("Campo obrigatório: vendedor");
    }
    return errors;
  };

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setImportResult(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false });
      if (!raw.length) {
        toast.error("Arquivo vazio");
        return;
      }
      const headerRow = raw[0];
      const headerMap = headerRow.map(mapHeader);
      // Verifica se ao menos os campos obrigatórios existem
      const missing = REQUIRED_FIELDS.filter((f) => !headerMap.includes(f));
      if (missing.length) {
        toast.error(`Faltam colunas obrigatórias: ${missing.join(", ")}`);
        setRows([]);
        return;
      }

      const parsed = [];
      for (let r = 1; r < raw.length; r++) {
        const line = raw[r];
        // Ignora linha totalmente vazia
        if (!line || line.every((c) => String(c ?? "").trim() === "")) continue;
        const data = {
          nome: "", contato: "", localidade: "", motivo: "",
          vendedor: "", observacoes: "", data_registro: "", reavaliar_em: "",
        };
        headerMap.forEach((canonical, i) => {
          if (!canonical) return;
          let val = line[i];
          if (canonical === "data_registro" || canonical === "reavaliar_em") {
            val = parseDateCell(val);
          }
          data[canonical] = typeof val === "string" ? val.trim() : (val ?? "");
        });
        // Vendedora: força próprio nome
        if (!admin && user?.vendedor_name) {
          data.vendedor = user.vendedor_name;
        }
        // default data_registro se vazio
        if (!data.data_registro) data.data_registro = new Date().toISOString().slice(0, 10);
        const errors = validateRow(data);
        parsed.push({ data, errors, ok: errors.length === 0, index: r });
      }
      setRows(parsed);
      if (parsed.length === 0) {
        toast.error("Nenhuma linha válida encontrada");
      } else {
        toast.success(`${parsed.length} linha(s) lida(s) — revise antes de importar`);
      }
    } catch (e) {
      toast.error("Falha ao ler o arquivo. Use CSV ou XLSX.");
    }
  };

  const validCount = rows.filter((r) => r.ok).length;
  const errorCount = rows.length - validCount;

  const handleImport = async () => {
    const validRows = rows.filter((r) => r.ok);
    if (validRows.length === 0) {
      toast.error("Nenhuma linha válida para importar");
      return;
    }
    setImporting(true);
    let ok = 0;
    let fail = 0;
    for (const r of validRows) {
      try {
        const payload = {
          vendedor: r.data.vendedor,
          nome: r.data.nome,
          contato: r.data.contato,
          localidade: r.data.localidade,
          motivo: r.data.motivo,
          observacoes: r.data.observacoes,
          data_registro: r.data.data_registro,
          reavaliar_em: r.data.reavaliar_em,
          status: "lead_futuro",
        };
        await createProspecto(payload);
        ok += 1;
      } catch (err) {
        console.error("Falha ao importar prospecto:", err);
        fail += 1;
      }
    }
    setImporting(false);
    setImportResult({ ok, fail });
    if (ok > 0) toast.success(`${ok} prospecto(s) importado(s)`);
    if (fail > 0) toast.error(`${fail} linha(s) falharam ao importar`);
    if (ok > 0 && onImported) onImported();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-importar-prospectos">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-primary" /> Importar prospectos
          </DialogTitle>
          <DialogDescription>
            Envie um arquivo <strong>CSV</strong> ou <strong>Excel (.xlsx)</strong> com as colunas obrigatórias:{" "}
            <span className="text-foreground font-medium">nome, contato, localidade, motivo</span>
            {admin ? " e vendedor" : ""}. Colunas opcionais: observacoes, data_registro, reavaliar_em.
          </DialogDescription>
        </DialogHeader>

        {!importResult && (
          <>
            {/* Ações */}
            <div className="flex flex-col sm:flex-row gap-3 py-2">
              <Button
                variant="outline"
                onClick={downloadTemplate}
                data-testid="btn-download-template"
                className="sm:w-auto"
              >
                <FileDown className="w-4 h-4 mr-2" /> Baixar modelo CSV
              </Button>
              <div className="flex-1">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="block w-full text-sm text-muted-foreground file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer border border-border rounded-md p-1"
                  data-testid="input-file-import"
                />
                {fileName && (
                  <div className="text-xs text-muted-foreground mt-1">Selecionado: {fileName}</div>
                )}
              </div>
            </div>

            {!admin && user?.vendedor_name && (
              <div className="text-xs text-muted-foreground rounded-md bg-muted/40 p-2 border border-border">
                Você é vendedora. Todos os prospectos importados serão atribuídos a você:{" "}
                <strong className="text-foreground">{user.vendedor_name}</strong>.
              </div>
            )}

            {/* Resumo do preview */}
            {rows.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap py-2">
                <Badge variant="outline" className="text-emerald-500 border-emerald-500/40">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {validCount} válida(s)
                </Badge>
                {errorCount > 0 && (
                  <Badge variant="outline" className="text-red-500 border-red-500/40">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    {errorCount} com erro(s)
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">Revise antes de confirmar</span>
              </div>
            )}

            {/* Preview */}
            {rows.length > 0 && (
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="thin-scroll overflow-x-auto max-h-[320px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-14">Linha</TableHead>
                        <TableHead>Vendedor</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Contato</TableHead>
                        <TableHead>Localidade</TableHead>
                        <TableHead>Motivo</TableHead>
                        <TableHead>Erros</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow
                          key={r.index}
                          className={r.ok ? "" : "bg-red-500/5"}
                          data-testid={`preview-row-${r.index}`}
                        >
                          <TableCell className="text-xs tabular-nums text-muted-foreground">{r.index + 1}</TableCell>
                          <TableCell className="text-sm">{r.data.vendedor || "—"}</TableCell>
                          <TableCell className="text-sm">{r.data.nome || "—"}</TableCell>
                          <TableCell className="text-sm">{r.data.contato || "—"}</TableCell>
                          <TableCell className="text-sm">{r.data.localidade || "—"}</TableCell>
                          <TableCell className="text-sm max-w-[160px] truncate" title={r.data.motivo}>
                            {r.data.motivo || "—"}
                          </TableCell>
                          <TableCell className="text-xs text-red-500">
                            {r.errors.join("; ") || "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Resultado */}
        {importResult && (
          <div className="py-4 space-y-2" data-testid="import-result">
            <div className="text-lg font-heading font-semibold">Importação concluída</div>
            <div className="text-sm">
              <CheckCircle2 className="inline w-4 h-4 text-emerald-500 mr-1" />
              {importResult.ok} prospecto(s) importado(s) com sucesso.
            </div>
            {importResult.fail > 0 && (
              <div className="text-sm">
                <AlertTriangle className="inline w-4 h-4 text-red-500 mr-1" />
                {importResult.fail} linha(s) falharam.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!importResult ? (
            <>
              <Button variant="outline" onClick={() => handleClose(false)}>Cancelar</Button>
              <Button
                onClick={handleImport}
                disabled={validCount === 0 || importing}
                data-testid="btn-confirm-import"
              >
                {importing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Importar {validCount > 0 ? `(${validCount})` : ""}
              </Button>
            </>
          ) : (
            <Button onClick={() => handleClose(false)} data-testid="btn-close-import">Fechar</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
